/**
 * CompetitorActivityStrip (was LeaderboardStrip).
 *
 * Slotted INSIDE each <TrendRow> on /trending, between the thesis text
 * and the velocity sparkline. Renders only when there is at least one
 * observably active or observably quiet uploaded competitor on the trend.
 *
 * ─── iter 92 · public-signal-only redesign ───────────────────────────
 * Brands upload their competitor set during profile onboarding. WAVE
 * therefore has the COMPETITOR'S PUBLIC SOCIAL HANDLES + VERTICAL, but
 * NOT their contracted creator roster, internal wave performance, or
 * proprietary metrics.
 *
 * Legitimate signals this component now surfaces:
 *   • Competitor BRAND NAME on a trend — observed from public posting
 *     activity from that brand's public handles.
 *   • Qualitative activity status — 'active' / 'quiet' / 'silent' —
 *     derived from observed public post volume from the brand's handles.
 *
 * Removed in iter 92 (fabricated — would require access to data WAVE
 * cannot legitimately see):
 *   • Competitor Pulse scores (Pulse is computed from `wave_assignments`;
 *     a brand not on WAVE has no Pulse).
 *   • Competitor creator counts (requires their contracted roster).
 *   • Trophy / rank display (depends on Pulse comparison).
 *   • "X and Y share N creators" (requires both rosters).
 *   • "N available creators like X's · FIND THEM" (requires their roster).
 *   • Projected-position chip "#N of M · projected pulse N"
 *     (depends on simulated competitor Pulse).
 *   • Competitor-gap chip "Outpace #1 in 18h"
 *     (depends on simulated competitor Pulse).
 *
 * Saturation gauge moved to the trend card's right panel (it belongs
 * with Brand Match / Momentum / Halflife). This component is now a
 * focused "what your uploaded competitors are doing on this trend
 * RIGHT NOW based on public data" surface — nothing else.
 */
import React from 'react';
import { Activity, AlertCircle, PauseCircle } from 'lucide-react';

const STATUS_CONFIG = {
  active: {
    label: 'Active',
    color: '#4ADE80',
    bg:    'rgba(74,222,128,0.10)',
    border: 'rgba(74,222,128,0.30)',
    Icon:  Activity,
    desc:  'observably posting on this trend',
  },
  quiet: {
    label: 'Quiet',
    color: '#FACC15',
    bg:    'rgba(250,204,21,0.08)',
    border: 'rgba(250,204,21,0.28)',
    Icon:  PauseCircle,
    desc:  'public posting has slowed on this trend',
  },
  silent: {
    label: 'Silent',
    color: '#94A3B8',
    bg:    'rgba(148,163,184,0.08)',
    border: 'rgba(148,163,184,0.25)',
    Icon:  AlertCircle,
    desc:  'no public posting from this brand on this trend in 14+ days',
  },
};

/**
 * Merge the trend's observable signals into a single, de-duplicated
 * competitor-activity list.
 *
 * - `competing_brands_live[]` → "active" (presence in the live set means
 *   WAVE has observed posting activity from the brand's public handles).
 *   When the backend ships an explicit `activity` value per brand, that
 *   takes precedence so we honour any future quiet/silent refinement.
 * - `competitor_whitespace.competitor` + `days_dark` → "quiet" (1–13d
 *   without observed public posting) or "silent" (14+d). Suppressed if
 *   the same brand is already in the active list.
 *
 * Returns []  when no uploaded competitor has any observable activity —
 * the caller renders nothing in that case (no empty chrome).
 */
function buildCompetitorActivity(trend) {
  const out = [];
  const seen = new Set();

  (trend.competing_brands_live || []).forEach((c) => {
    if (!c?.name) return;
    const status = (c.activity || 'active').toLowerCase();
    out.push({ name: c.name, status });
    seen.add(c.name.toLowerCase());
  });

  const w = trend.competitor_whitespace;
  if (w?.is_open && w?.competitor && !seen.has(w.competitor.toLowerCase())) {
    const days = w.days_dark || 0;
    out.push({
      name:   w.competitor,
      status: days >= 14 ? 'silent' : 'quiet',
    });
  }

  return out;
}

function CompetitorRow({ name, status, testId }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.active;
  const StatusIcon = cfg.Icon;
  return (
    <div
      data-testid={testId}
      data-status={status}
      className="flex items-center gap-2 px-2 py-1.5 rounded-lg"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border:     '1px solid rgba(255,255,255,0.04)',
      }}
    >
      <span
        className="text-[11px] font-bold flex-1 truncate text-gray-200"
        data-testid={`${testId}-name`}
      >
        {name}
      </span>
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-black uppercase tracking-wider"
        style={{
          background: cfg.bg,
          color:      cfg.color,
          border:     `1px solid ${cfg.border}`,
        }}
        data-testid={`${testId}-status`}
        title={cfg.desc}
      >
        <StatusIcon className="h-2.5 w-2.5" />
        {cfg.label}
      </span>
    </div>
  );
}

export default function CompetitorActivityStrip({ trend }) {
  const competitors = buildCompetitorActivity(trend);
  if (competitors.length === 0) return null;

  return (
    <div
      data-testid="competitor-activity-strip"
      data-competitor-count={competitors.length}
      className="rounded-xl p-3 space-y-2"
      style={{
        background: '#0E0F12',
        border:     '1px solid rgba(255,255,255,0.05)',
      }}
    >
      <div className="flex items-center justify-between">
        <h4
          className="text-[10px] uppercase tracking-[0.18em] font-black text-gray-400"
          data-testid="competitor-activity-heading"
        >
          Competitor activity on this trend
        </h4>
        <span
          className="text-[9px] text-gray-500"
          data-testid="competitor-activity-source"
          title="Activity is observed from each competitor's public social posting — not their internal wave data."
        >
          public signal
        </span>
      </div>

      <div className="space-y-1">
        {competitors.map((c, i) => (
          <CompetitorRow
            key={`${c.name}-${i}`}
            name={c.name}
            status={c.status}
            testId={`competitor-activity-row-${i}`}
          />
        ))}
      </div>
    </div>
  );
}

// Backward-compat export — anything still importing `LeaderboardStrip`
// receives the new component. Keeps the iter-72 import site working
// while we let dependents migrate at their own pace.
export { CompetitorActivityStrip as LeaderboardStrip };
